/**
 * Customer copy for the web chat, keyed per docs/standards/i18n.md §2.
 * English is the source; French uses "vous" and French typography.
 */
const en = {
  "chat.header.subtitle": "Secure payments assistant",
  "chat.header.history": "Conversation history",
  "chat.welcome": "Hi {first_name} 👋 What can I help you with today?",

  "chat.composer.label": "Message",
  "chat.composer.placeholder": "Message BluPay",
  "chat.composer.send": "Send message",
  "chat.composer.attach": "Add attachment",

  "chat.step.typing": "BluPay is typing…",
  "chat.composer.placeholder_secure": "Enter your PIN only in the secure box above",
  "chat.step.understanding": "Reading your message…",
  "chat.step.finding_recipient": "Finding your recipient…",
  "chat.step.getting_quote": "Getting a quote…",
  "chat.step.preparing_transfer": "Preparing your transfer…",
  "chat.step.confirming_transfer": "Confirming your transfer…",

  "chat.message.failed": "Not sent.",
  "chat.message.retry": "Retry",
  "chat.message.load_earlier": "Load earlier messages",
  "chat.message.loading_earlier": "Loading…",
  "chat.message.daily_limit_reached":
    "You've sent the most chat messages allowed today. You can still use the app menus.",
  "chat.message.pin_not_allowed":
    "Never type your PIN in the chat. Enter it only in the secure PIN box.",
  "chat.message.card_details_not_allowed":
    "Never type card numbers or bank details in the chat. Use the forms in the app.",
  "chat.message.send_failed": "We couldn't send that message. Try again.",

  "chat.history.title": "Conversations",
  "chat.history.new": "New conversation",
  "chat.history.untitled": "New conversation",
  "chat.history.empty": "No conversations yet.",
  "chat.history.load_failed": "We couldn't load your conversations.",
  "chat.history.rename": "Rename",
  "chat.history.rename_label": "Conversation name",
  "chat.history.save": "Save",
  "chat.history.cancel": "Cancel",
  "chat.history.archive": "Archive",
  "chat.history.back": "Back to chat",
  "chat.history.you": "You: {text}",

  "chat.reply.send_money": "Send money",
  "chat.reply.bank": "Bank transfer",
  "chat.reply.card": "Card",
  "chat.reply.new_recipient": "+ New recipient",
  "chat.reply.not_now": "Not now",
  "chat.reply.continue": "Continue",
  "chat.reply.change_amount": "Change amount",
  "chat.reply.cancel": "Cancel",
  "chat.reply.retry": "Try again",
  "chat.reply.main_menu": "Main menu",

  "chat.home.next": "What would you like to do next?",
  "chat.home.cancelled": "Cancelled. What would you like to do next?",
  "chat.help.menu":
    "I can help you send money, check rates, view transfers, manage recipients, or get support.",
  "chat.help.unknown":
    "I can help you send money, check rates, view your activity, manage recipients, or get support.",

  "chat.nav.activity": "Here’s your recent activity.",
  "chat.nav.recipients": "Here are your recipients.",
  "chat.nav.rates": "Here’s the exchange-rate calculator.",
  "chat.nav.profile": "Here’s your profile.",
  "chat.nav.security": "Here are your security settings.",
  "chat.nav.kyc": "Here’s your identity verification.",
  "chat.nav.support": "I’ll take you to support.",
  "chat.nav.notifications": "Here are your notifications.",

  "chat.kyc.required": "You'll need to verify your identity before you can send money.",
  "chat.kyc.pending":
    "Your identity verification is still in progress. You'll be able to send money once it's approved.",
  "chat.kyc.rejected":
    "We couldn't complete your identity verification. Please review it before trying again.",
  "chat.kyc.expired": "Your identity verification needs to be updated before you can send money.",
  "chat.kyc.verify": "Verify identity",
  "chat.kyc.check": "Check verification",
  "chat.kyc.review": "Review verification",
  "chat.kyc.update": "Update verification",
  "chat.kyc.open": "Let's get your verification sorted.",
  "chat.kyc.required_short": "Identity verification is required before sending money.",

  "chat.recipient.fallback_name": "your recipient",
  "chat.recipient.choose": "Who would you like to send money to?",
  "chat.recipient.first": "Let's add your first recipient.",
  "chat.recipient.add_new": "Let's add someone new.",
  "chat.recipient.not_found": "I couldn't find that recipient. Choose one below or add someone new.",
  "chat.recipient.details_missing":
    "Some recipient payment details are missing. Please check the recipient before continuing.",
  "chat.recipient.added": "{recipient} has been added.",
  "chat.recipient.create_cancelled": "No problem. Choose a recipient.",
  "chat.recipient.choose_again": "Sure — choose a recipient.",
  "chat.recipient.form_pending": "Complete the recipient form above, or cancel.",
  "chat.recipient.required": "Please choose a recipient.",
  "chat.recipient.no_payment_method": "This recipient doesn't have a payment method available.",

  "chat.amount.prompt": "How much {currency} would you like to send to {recipient}?",
  "chat.amount.invalid": "Enter the amount in {currency}, for example 100 {currency}.",
  "chat.amount.accepted": "Got it — {amount}.",
  "chat.amount.change": "Sure. How much {currency} would you like to send instead?",
  "chat.amount.required": "Enter a valid amount.",

  "chat.quote.getting": "Let me get the latest rate for {amount}.",
  "chat.quote.receive": "{recipient} would receive approximately {amount}.",
  "chat.quote.wait": "One moment, I'm getting your quote. Type cancel to stop.",
  "chat.quote.failed":
    "I couldn't get the latest rate just now. Please enter the amount again or cancel.",

  "chat.funding.prompt": "How would you like to pay?",
  "chat.funding.choose": "Choose bank transfer or card.",

  "chat.review.check": "Please check everything looks right.",
  "chat.review.options": "You can continue, change the amount, or cancel.",

  "chat.transfer.preparing": "Preparing your transfer…",
  "chat.transfer.ready": "Ready when you are.",
  "chat.transfer.create_failed":
    "I couldn't create that transfer. Please check the details and try again.",
  "chat.transfer.create_error": "I couldn't create that transfer. Please try again.",
  "chat.transfer.bank_confirmed": "Transfer confirmed. Use the bank details below to fund it.",
  "chat.transfer.card_ready": "Your transfer is ready for card funding.",
  "chat.transfer.confirm_failed":
    "I couldn't confirm the transfer. Please check your PIN and try again.",
  "chat.transfer.confirm_error":
    "Something went wrong confirming this transfer. Please try again.",
  "chat.transfer.use_secure_controls": "Use the secure transfer controls above, or cancel.",
  "chat.pin.invalid": "Enter your 6-digit transaction PIN.",
};

const fr = {
  "chat.header.subtitle": "Assistant de paiement sécurisé",
  "chat.header.history": "Historique des conversations",
  "chat.welcome": "Bonjour {first_name} 👋 Comment puis-je vous aider aujourd'hui\u00a0?",

  "chat.composer.label": "Message",
  "chat.composer.placeholder": "Écrire à BluPay",
  "chat.composer.send": "Envoyer le message",
  "chat.composer.attach": "Ajouter une pièce jointe",

  "chat.step.typing": "BluPay écrit…",
  "chat.composer.placeholder_secure": "Saisissez votre code PIN uniquement dans la zone sécurisée ci-dessus",
  "chat.step.understanding": "Lecture de votre message…",
  "chat.step.finding_recipient": "Recherche de votre bénéficiaire…",
  "chat.step.getting_quote": "Obtention d'un devis…",
  "chat.step.preparing_transfer": "Préparation de votre transfert…",
  "chat.step.confirming_transfer": "Confirmation de votre transfert…",

  "chat.message.failed": "Non envoyé.",
  "chat.message.retry": "Réessayer",
  "chat.message.load_earlier": "Charger les messages précédents",
  "chat.message.loading_earlier": "Chargement…",
  "chat.message.daily_limit_reached":
    "Vous avez envoyé le nombre maximal de messages autorisé aujourd'hui. Vous pouvez toujours utiliser les menus de l'application.",
  "chat.message.pin_not_allowed":
    "Ne saisissez jamais votre code PIN dans la conversation. Saisissez-le uniquement dans la zone sécurisée.",
  "chat.message.card_details_not_allowed":
    "Ne saisissez jamais de numéro de carte ni de coordonnées bancaires dans la conversation. Utilisez les formulaires de l'application.",
  "chat.message.send_failed": "Nous n'avons pas pu envoyer ce message. Réessayez.",

  "chat.history.title": "Conversations",
  "chat.history.new": "Nouvelle conversation",
  "chat.history.untitled": "Nouvelle conversation",
  "chat.history.empty": "Aucune conversation pour le moment.",
  "chat.history.load_failed": "Nous n'avons pas pu charger vos conversations.",
  "chat.history.rename": "Renommer",
  "chat.history.rename_label": "Nom de la conversation",
  "chat.history.save": "Enregistrer",
  "chat.history.cancel": "Annuler",
  "chat.history.archive": "Archiver",
  "chat.history.back": "Retour à la conversation",
  "chat.history.you": "Vous\u00a0: {text}",

  "chat.reply.send_money": "Envoyer de l'argent",
  "chat.reply.bank": "Virement bancaire",
  "chat.reply.card": "Carte",
  "chat.reply.new_recipient": "+ Nouveau bénéficiaire",
  "chat.reply.not_now": "Pas maintenant",
  "chat.reply.continue": "Continuer",
  "chat.reply.change_amount": "Modifier le montant",
  "chat.reply.cancel": "Annuler",
  "chat.reply.retry": "Réessayer",
  "chat.reply.main_menu": "Menu principal",

  "chat.home.next": "Que souhaitez-vous faire ensuite\u00a0?",
  "chat.home.cancelled": "Annulé. Que souhaitez-vous faire ensuite\u00a0?",
  "chat.help.menu":
    "Je peux vous aider à envoyer de l'argent, consulter les taux, voir vos transferts, gérer vos bénéficiaires ou contacter le support.",
  "chat.help.unknown":
    "Je peux vous aider à envoyer de l'argent, consulter les taux, voir votre activité, gérer vos bénéficiaires ou contacter le support.",

  "chat.nav.activity": "Voici votre activité récente.",
  "chat.nav.recipients": "Voici vos bénéficiaires.",
  "chat.nav.rates": "Voici le calculateur de taux de change.",
  "chat.nav.profile": "Voici votre profil.",
  "chat.nav.security": "Voici vos paramètres de sécurité.",
  "chat.nav.kyc": "Voici votre vérification d'identité.",
  "chat.nav.support": "Je vous emmène vers le support.",
  "chat.nav.notifications": "Voici vos notifications.",

  "chat.kyc.required":
    "Vous devez vérifier votre identité avant de pouvoir envoyer de l'argent.",
  "chat.kyc.pending":
    "Votre vérification d'identité est en cours. Vous pourrez envoyer de l'argent dès qu'elle sera approuvée.",
  "chat.kyc.rejected":
    "Nous n'avons pas pu finaliser votre vérification d'identité. Vérifiez-la avant de réessayer.",
  "chat.kyc.expired":
    "Votre vérification d'identité doit être mise à jour avant que vous puissiez envoyer de l'argent.",
  "chat.kyc.verify": "Vérifier mon identité",
  "chat.kyc.check": "Voir la vérification",
  "chat.kyc.review": "Revoir la vérification",
  "chat.kyc.update": "Mettre à jour la vérification",
  "chat.kyc.open": "Occupons-nous de votre vérification.",
  "chat.kyc.required_short":
    "La vérification d'identité est obligatoire avant d'envoyer de l'argent.",

  "chat.recipient.fallback_name": "votre bénéficiaire",
  "chat.recipient.choose": "À qui souhaitez-vous envoyer de l'argent\u00a0?",
  "chat.recipient.first": "Ajoutons votre premier bénéficiaire.",
  "chat.recipient.add_new": "Ajoutons un nouveau bénéficiaire.",
  "chat.recipient.not_found":
    "Je n'ai pas trouvé ce bénéficiaire. Choisissez-en un ci-dessous ou ajoutez-en un nouveau.",
  "chat.recipient.details_missing":
    "Il manque des coordonnées de paiement pour ce bénéficiaire. Vérifiez-le avant de continuer.",
  "chat.recipient.added": "{recipient} a été ajouté.",
  "chat.recipient.create_cancelled": "Pas de problème. Choisissez un bénéficiaire.",
  "chat.recipient.choose_again": "D'accord, choisissez un bénéficiaire.",
  "chat.recipient.form_pending": "Remplissez le formulaire du bénéficiaire ci-dessus, ou annulez.",
  "chat.recipient.required": "Choisissez un bénéficiaire.",
  "chat.recipient.no_payment_method":
    "Ce bénéficiaire n'a pas de moyen de paiement disponible.",

  "chat.amount.prompt": "Combien de {currency} souhaitez-vous envoyer à {recipient}\u00a0?",
  "chat.amount.invalid": "Saisissez le montant en {currency}, par exemple 100 {currency}.",
  "chat.amount.accepted": "C'est noté\u00a0: {amount}.",
  "chat.amount.change": "D'accord. Combien de {currency} souhaitez-vous envoyer à la place\u00a0?",
  "chat.amount.required": "Saisissez un montant valide.",

  "chat.quote.getting": "Je récupère le dernier taux pour {amount}.",
  "chat.quote.receive": "{recipient} recevrait environ {amount}.",
  "chat.quote.wait": "Un instant, je récupère votre devis. Tapez «\u00a0annuler\u00a0» pour arrêter.",
  "chat.quote.failed":
    "Je n'ai pas pu obtenir le dernier taux. Saisissez à nouveau le montant ou annulez.",

  "chat.funding.prompt": "Comment souhaitez-vous payer\u00a0?",
  "chat.funding.choose": "Choisissez virement bancaire ou carte.",

  "chat.review.check": "Vérifiez que tout est correct.",
  "chat.review.options": "Vous pouvez continuer, modifier le montant ou annuler.",

  "chat.transfer.preparing": "Préparation de votre transfert…",
  "chat.transfer.ready": "Prêt quand vous l'êtes.",
  "chat.transfer.create_failed":
    "Je n'ai pas pu créer ce transfert. Vérifiez les détails et réessayez.",
  "chat.transfer.create_error": "Je n'ai pas pu créer ce transfert. Réessayez.",
  "chat.transfer.bank_confirmed":
    "Transfert confirmé. Utilisez les coordonnées bancaires ci-dessous pour le financer.",
  "chat.transfer.card_ready": "Votre transfert est prêt à être financé par carte.",
  "chat.transfer.confirm_failed":
    "Je n'ai pas pu confirmer le transfert. Vérifiez votre code PIN et réessayez.",
  "chat.transfer.confirm_error":
    "Un problème est survenu lors de la confirmation de ce transfert. Réessayez.",
  "chat.transfer.use_secure_controls":
    "Utilisez les commandes sécurisées du transfert ci-dessus, ou annulez.",
  "chat.pin.invalid": "Saisissez votre code PIN de transaction à 6 chiffres.",
};

export const catalogs = { en, fr };

export function getChatLocale() {
  const stored =
    typeof localStorage !== "undefined" ? localStorage.getItem("blupay.locale") : null;

  if (stored === "en" || stored === "fr") return stored;

  const language = typeof navigator !== "undefined" ? navigator.language || "" : "";
  return language.toLowerCase().startsWith("fr") ? "fr" : "en";
}

export function chatText(key, values = {}, locale = getChatLocale()) {
  const template = catalogs[locale]?.[key] ?? en[key] ?? key;

  return template.replace(/\{(\w+)\}/g, (match, name) =>
    values[name] === undefined ? match : String(values[name])
  );
}

/**
 * Copy for a chat API error, chosen by its reason. Falls back to the
 * generic send failure, never to server text.
 */
export function chatErrorText(error) {
  if (error?.reason && en[error.reason]) return chatText(error.reason);
  return chatText("chat.message.send_failed");
}
